import {
  type ExtractDocumentTypeFromTypedRxJsonSchema,
  type RxCollection,
  type RxJsonSchema,
  toTypedRxJsonSchema,
} from "rxdb";
import { useRxCollection, type useRxData } from "rxdb-hooks";
import { SYNCABLE_META } from "./common";
import type {
  EpocusConsentAudienceValue,
  EpocusConsentItemKindValue,
} from "./epocus-patient-consent";

export const RXDB_EPOCUS_CONSENT_DOCUMENT_COLLECTION_NAME =
  "epocus-consent-document";

export const useRxCollectionEpocusConsentDocument =
  (): RxCollection<EpocusConsentDocument> | null =>
    useRxCollection<EpocusConsentDocument>(
      RXDB_EPOCUS_CONSENT_DOCUMENT_COLLECTION_NAME,
    );

type UseRxData = Parameters<typeof useRxData<EpocusConsentDocument>>;
export type EpocusConsentDocumentQueryConstructor = UseRxData["1"];

export type EpocusConsentDocumentItem = {
  id: string;
  audience: EpocusConsentAudienceValue;
  kind: EpocusConsentItemKindValue;
  ordinal: number;
  bodyText: string;
};

const EPOCUS_CONSENT_DOCUMENT_SCHEMA_LITERAL = {
  title: "epocus consent document schema",
  version: 0,
  description:
    "Versioned, pull-only Terms of Registration content — provisioned server-side, never authored on device",
  type: "object",
  primaryKey: "id",
  properties: {
    id: { type: "string", maxLength: 100 },
    version: { type: "number" },
    // biome-ignore lint/suspicious/noExplicitAny: EpocusConsentDocumentItem[] — shape enforced by TypeScript override below
    items: { type: "array", items: { type: "object" } } as any,
    meta: SYNCABLE_META,
  },
  required: ["id", "version", "items", "meta"],
  /*
   * biome-ignore lint/suspicious/noExplicitAny: We infer the DB type from this
   * object, the satisfies just provides us type inference for this object.
   */
} as const satisfies RxJsonSchema<any>;

const schemaTyped = toTypedRxJsonSchema(EPOCUS_CONSENT_DOCUMENT_SCHEMA_LITERAL);

type RawEpocusConsentDocument = ExtractDocumentTypeFromTypedRxJsonSchema<
  typeof schemaTyped
>;

export type EpocusConsentDocument = Omit<RawEpocusConsentDocument, "items"> & {
  items: EpocusConsentDocumentItem[];
};

export type EpocusConsentDocumentCollection =
  RxCollection<EpocusConsentDocument>;

export const EPOCUS_CONSENT_DOCUMENT_SCHEMA: RxJsonSchema<EpocusConsentDocument> =
  EPOCUS_CONSENT_DOCUMENT_SCHEMA_LITERAL as unknown as RxJsonSchema<EpocusConsentDocument>;
